/**
 * ConditionSettingsEditor
 *
 * Reusable component for editing verification sections (groups + fields).
 * Used in both the FormAddConditionWizard and ConfigureConditions.
 */

// react
import { useContext, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Chip,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import type { ChipProps } from '@mui/material';

// icons
import {
	Add as IconAdd,
	AttachFile as IconDocument,
	Close as IconClose,
	ExpandMore as IconExpandMore,
	Group as IconGroup,
	KeyboardArrowDown as IconArrowDown,
	KeyboardArrowUp as IconArrowUp,
	QuestionAnswer as IconDataRequest,
	Rule as IconRule,
	Settings as IconSettings,
} from '@mui/icons-material';

// types
import type {
	ComparisonOperator,
	DataRequestField,
	DataRequestFieldType,
	VerificationSection,
} from '../../../@types';

// context
import { ContextUsersPermissions } from '../../pages/Project/Project.context';

// dialog
import { DialogFieldSettings } from '../../dialogs/DialogFieldSettings/DialogFieldSettings';

// evaluator
import { describeExpectation, normalizeExpectation } from '../../../utils/expectationEvaluator';

// Constants

const FIELD_TYPE_COLORS: Record<string, string> = {
	document: '#1976d2',
	yesno: '#7b1fa2',
	text: '#546e7a',
	number: '#00897b',
	date: '#e65100',
	select: '#5c6bc0',
};

// Field-type accent mapped to design-system twoTone tokens for the type chip.
const FIELD_TYPE_CHIP_COLORS: Record<string, ChipProps['color']> = {
	document: 'info',
	yesno: 'purple',
	text: 'neutral',
	number: 'teal',
	date: 'warning',
	select: 'primary',
};

const getFieldTypeLabels = (t: (k: string) => string): Record<string, string> => ({
	document: t('spendPlan.conditions.editor.fieldDocument'),
	yesno: t('spendPlan.conditions.editor.fieldYesNo'),
	text: t('spendPlan.conditions.editor.fieldText'),
	number: t('spendPlan.conditions.editor.fieldNumber'),
	date: t('spendPlan.conditions.editor.fieldDate'),
	select: t('spendPlan.conditions.editor.fieldDropdown'),
});

// Props

export interface ConditionSettingsEditorProps {
	/** Current verification sections state */
	verificationSections: VerificationSection[];
	/** Callback when sections change */
	onChange: (sections: VerificationSection[]) => void;
}

// Component

export const ConditionSettingsEditor = ({
	verificationSections,
	onChange,
}: ConditionSettingsEditorProps) => {
	const { t } = useTranslation();
	const { usersPermissions: projectUsers } = useContext(ContextUsersPermissions);

	// local UI state
	const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

	// field settings dialog state
	const [settingsField, setSettingsField] = useState<DataRequestField | null>(null);
	const [settingsHandlers, setSettingsHandlers] = useState<{
		onUpdate: (
			fieldId: string,
			property: keyof DataRequestField,
			value: DataRequestField[keyof DataRequestField]
		) => void;
		onUpdateOptions: (fieldId: string, optionsText: string) => void;
	} | null>(null);

	// =========================================================================
	// Section handlers
	// =========================================================================

	const handleAddSection = () => {
		const newSection: VerificationSection = {
			id: `section-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
			name: '',
			assignedTo: null,
			assignedGroupId: null,
			fields: [],
			status: 'PENDING',
			verifiedAt: null,
			verifiedBy: null,
		};
		onChange([...verificationSections, newSection]);
		setExpandedSections((prev) => ({ ...prev, [newSection.id]: true }));
	};

	const handleRemoveSection = (sectionId: string) => {
		onChange(verificationSections.filter((s) => s.id !== sectionId));
	};

	const handleUpdateSectionName = (sectionId: string, name: string) => {
		onChange(verificationSections.map((s) => (s.id === sectionId ? { ...s, name } : s)));
	};

	const handleUpdateSectionAssignee = (sectionId: string, assignedTo: number | null) => {
		onChange(verificationSections.map((s) => (s.id === sectionId ? { ...s, assignedTo } : s)));
	};

	const handleAddFieldToSection = (sectionId: string, fieldType: DataRequestFieldType) => {
		const defaults: Record<DataRequestFieldType, Partial<DataRequestField>> = {
			text: { label: '', placeholder: 'Enter text response' },
			number: { label: '', placeholder: 'Enter a numeric value', unit: '' },
			date: { label: '', placeholder: 'Select a date' },
			select: { label: '', options: [] },
			yesno: {
				label: t('spendPlan.conditions.editor.verificationQuestion'),
				placeholder: '',
				expectedResponse: { operator: 'eq' as ComparisonOperator, value: 'yes' },
			},
			document: {
				label: 'Supporting Document',
				placeholder: 'Upload a supporting document',
				required: true,
				maxDocuments: 1,
			},
		};
		const newField: DataRequestField = {
			id: `field-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
			fieldType,
			required: true,
			...defaults[fieldType],
			label: defaults[fieldType].label || '',
		};
		onChange(
			verificationSections.map((s) =>
				s.id === sectionId ? { ...s, fields: [...s.fields, newField] } : s
			)
		);
	};

	const handleRemoveFieldFromSection = (sectionId: string, fieldId: string) => {
		onChange(
			verificationSections.map((s) =>
				s.id === sectionId ? { ...s, fields: s.fields.filter((f) => f.id !== fieldId) } : s
			)
		);
	};

	const handleUpdateSectionField = (
		sectionId: string,
		fieldId: string,
		property: keyof DataRequestField,
		value: DataRequestField[keyof DataRequestField]
	) => {
		onChange(
			verificationSections.map((s) =>
				s.id === sectionId
					? {
							...s,
							fields: s.fields.map((f) => (f.id === fieldId ? { ...f, [property]: value } : f)),
						}
					: s
			)
		);
	};

	const handleUpdateSectionFieldOptions = (
		sectionId: string,
		fieldId: string,
		optionsText: string
	) => {
		const options = optionsText
			.split('\n')
			.map((opt) => opt.trim())
			.filter((opt) => opt.length > 0);
		handleUpdateSectionField(sectionId, fieldId, 'options', options);
	};

	// =========================================================================
	// Field settings dialog
	// =========================================================================

	const openFieldSettings = (
		field: DataRequestField,
		handlers: {
			onUpdate: (
				fieldId: string,
				property: keyof DataRequestField,
				value: DataRequestField[keyof DataRequestField]
			) => void;
			onUpdateOptions: (fieldId: string, optionsText: string) => void;
		}
	) => {
		setSettingsField(field);
		setSettingsHandlers(handlers);
	};

	const closeFieldSettings = () => {
		setSettingsField(null);
		setSettingsHandlers(null);
	};

	const handleFieldSettingsSave = (updatedField: DataRequestField) => {
		if (!settingsField || !settingsHandlers) return;
		const fieldId = settingsField.id;
		const keys: (keyof DataRequestField)[] = [
			'label',
			'fieldType',
			'placeholder',
			'unit',
			'required',
			'allowOverride',
			'expectedResponse',
			'maxDocuments',
			'allowSupplierCompletion',
		];
		for (const key of keys) {
			if (updatedField[key] !== settingsField[key]) {
				settingsHandlers.onUpdate(fieldId, key, updatedField[key]);
			}
		}
		if (updatedField.fieldType === 'select') {
			settingsHandlers.onUpdateOptions(fieldId, updatedField.options?.join('\n') || '');
		}
		closeFieldSettings();
	};

	// =========================================================================
	// Render helpers
	// =========================================================================

	const renderFieldTypeToolbar = (onAdd: (fieldType: DataRequestFieldType) => void) => (
		<Box
			sx={{
				display: 'flex',
				flexWrap: 'wrap',
				gap: 0.75,
				p: 1.5,
				bgcolor: 'grey.50',
				borderRadius: 2,
				border: '1px dashed',
				borderColor: 'grey.300',
			}}>
			<Typography
				variant="caption"
				sx={{
					width: '100%',
					color: 'text.secondary',
					fontWeight: 600,
					textTransform: 'uppercase',
					letterSpacing: '0.05em',
					mb: 0.25,
				}}>
				Add requirement
			</Typography>
			{(
				[
					{ type: 'document' as DataRequestFieldType, icon: <IconDocument fontSize="small" /> },
					{ type: 'yesno' as DataRequestFieldType, icon: <IconDataRequest fontSize="small" /> },
					{ type: 'text' as DataRequestFieldType, icon: <IconAdd fontSize="small" /> },
					{ type: 'number' as DataRequestFieldType, icon: <IconAdd fontSize="small" /> },
					{ type: 'date' as DataRequestFieldType, icon: <IconAdd fontSize="small" /> },
					{ type: 'select' as DataRequestFieldType, icon: <IconAdd fontSize="small" /> },
				] as const
			).map(({ type, icon }) => (
				<Chip
					key={type}
					clickable
					icon={icon}
					label={getFieldTypeLabels(t)[type]}
					onClick={() => onAdd(type)}
					color={FIELD_TYPE_CHIP_COLORS[type] || 'neutral'}
					size="small"
					variant="twoTone"
				/>
			))}
		</Box>
	);

	const renderFieldCard = (
		field: DataRequestField,
		fieldIndex: number,
		totalFields: number,
		handlers: {
			onMove: (fieldId: string, direction: 'up' | 'down') => void;
			onRemove: (fieldId: string) => void;
			onUpdate: (
				fieldId: string,
				property: keyof DataRequestField,
				value: DataRequestField[keyof DataRequestField]
			) => void;
			onUpdateOptions: (fieldId: string, optionsText: string) => void;
		}
	) => {
		const accentColor = FIELD_TYPE_COLORS[field.fieldType] || '#546e7a';
		const expectationRules = normalizeExpectation(field.expectedResponse);
		const hasRules = expectationRules.length > 0;
		return (
			<Paper
				key={field.id}
				elevation={0}
				sx={{
					bgcolor: 'background.paper',
					border: '1px solid',
					borderColor: 'grey.200',
					borderLeft: `3px solid ${accentColor}`,
					borderRadius: '0 8px 8px 0',
					overflow: 'hidden',
					transition: 'box-shadow 0.15s, border-color 0.15s',
					'&:hover': {
						boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
						borderColor: 'grey.300',
					},
				}}>
				{/* Header */}
				<Stack
					direction="row"
					alignItems="center"
					spacing={0.75}
					sx={{
						px: 1.5,
						py: 0.75,
						bgcolor: `${accentColor}08`,
						borderBottom: '1px solid',
						borderColor: 'grey.100',
					}}>
					<Stack direction="row" spacing={0} sx={{ mr: 0.25 }}>
						<IconButton
							disabled={fieldIndex === 0}
							onClick={() => handlers.onMove(field.id, 'up')}
							size="small"
							sx={{ p: 0.25, color: 'text.secondary' }}>
							<IconArrowUp sx={{ fontSize: 18 }} />
						</IconButton>
						<IconButton
							disabled={fieldIndex === totalFields - 1}
							onClick={() => handlers.onMove(field.id, 'down')}
							size="small"
							sx={{ p: 0.25, color: 'text.secondary' }}>
							<IconArrowDown sx={{ fontSize: 18 }} />
						</IconButton>
					</Stack>
					<Typography
						variant="caption"
						sx={{
							color: 'text.disabled',
							fontWeight: 600,
							minWidth: 18,
							textAlign: 'center',
						}}>
						{fieldIndex + 1}
					</Typography>
					<Chip
						label={getFieldTypeLabels(t)[field.fieldType] || field.fieldType}
						color={FIELD_TYPE_CHIP_COLORS[field.fieldType] || 'neutral'}
						size="small"
						variant="twoTone"
					/>
					{field.required ? (
						<Tooltip title="This field is required. Click to make it optional." arrow>
							<Chip
								label={t('spendPlan.conditions.editor.labelRequired')}
								color="error"
								size="small"
								variant="twoTone"
								onClick={(e) => {
									e.stopPropagation();
									handlers.onUpdate(field.id, 'required', false);
								}}
							/>
						</Tooltip>
					) : (
						<Tooltip title="This field is optional. Click to make it required." arrow>
							<Chip
								label="Optional"
								color="default"
								size="small"
								variant="twoTone"
								onClick={(e) => {
									e.stopPropagation();
									handlers.onUpdate(field.id, 'required', true);
								}}
							/>
						</Tooltip>
					)}
					{field.required && field.allowOverride && (
						<Chip label="Overridable" color="warning" size="small" variant="twoTone" />
					)}
					{field.fieldType === 'number' && field.unit && (
						<Chip label={`Unit: ${field.unit}`} color="teal" size="small" variant="twoTone" />
					)}
					{field.fieldType === 'document' &&
						(() => {
							const max = field.maxDocuments ?? 1;
							return (
								<Chip
									label={max === 0 ? 'Unlimited docs' : `Max: ${max}`}
									color="info"
									size="small"
									variant="twoTone"
								/>
							);
						})()}
					{hasRules && (
						<Chip
							icon={<IconRule sx={{ fontSize: 14 }} />}
							label={`${expectationRules.length} rule${expectationRules.length > 1 ? 's' : ''}`}
							color="success"
							size="small"
							variant="twoTone"
						/>
					)}
					{field.allowSupplierCompletion ? (
						<Tooltip
							title="Suppliers can complete this field from the supplier portal. Click to make it internal-only."
							arrow>
							<Chip
								label={t('spendPlan.conditions.editor.labelInternalSupplier')}
								color="info"
								size="small"
								variant="twoTone"
								onClick={(e) => {
									e.stopPropagation();
									handlers.onUpdate(field.id, 'allowSupplierCompletion', false);
								}}
							/>
						</Tooltip>
					) : (
						<Tooltip
							title="Only internal users can complete this field. Click to also allow the supplier to complete it from the supplier portal."
							arrow>
							<Chip
								label={t('spendPlan.conditions.editor.labelInternal')}
								color="default"
								size="small"
								variant="twoTone"
								onClick={(e) => {
									e.stopPropagation();
									handlers.onUpdate(field.id, 'allowSupplierCompletion', true);
								}}
							/>
						</Tooltip>
					)}
					<Box sx={{ flex: 1 }} />
					<IconButton
						onClick={() => handlers.onRemove(field.id)}
						size="small"
						sx={{ p: 0.25, color: 'grey.400', '&:hover': { color: 'error.main' } }}>
						<IconClose sx={{ fontSize: 16 }} />
					</IconButton>
				</Stack>
				{/* Body */}
				<Box sx={{ px: 1.5, py: 1.25 }}>
					<Stack direction="row" alignItems="center" spacing={1}>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<TextField
								fullWidth
								label={
									field.fieldType === 'yesno'
										? t('spendPlan.conditions.editor.questionLabel')
										: field.fieldType === 'document'
											? 'Document Label'
											: 'Field Label'
								}
								onChange={(e) => handlers.onUpdate(field.id, 'label', e.target.value)}
								placeholder={
									field.fieldType === 'yesno'
										? 'e.g., Has the bank guarantee been received?'
										: field.fieldType === 'document'
											? 'e.g., Invoice, Progress Report'
											: 'e.g., Road completion length'
								}
								required
								size="small"
								value={field.label}
							/>
							{hasRules && (
								<Typography
									variant="caption"
									sx={{
										display: 'block',
										mt: 0.5,
										color: '#2e7d32',
										fontStyle: 'italic',
									}}>
									{t('spendPlan.conditions.editor.expected')}{' '}
									{describeExpectation(expectationRules)}
								</Typography>
							)}
						</Box>
						<IconButton
							onClick={() =>
								openFieldSettings(field, {
									onUpdate: handlers.onUpdate,
									onUpdateOptions: handlers.onUpdateOptions,
								})
							}
							size="small"
							sx={{
								bgcolor: `${accentColor}10`,
								color: accentColor,
								'&:hover': { bgcolor: `${accentColor}20` },
							}}
							title="Field settings">
							<IconSettings sx={{ fontSize: 20 }} />
						</IconButton>
					</Stack>
				</Box>
			</Paper>
		);
	};

	// =========================================================================
	// Main render
	// =========================================================================

	return (
		<>
			<Stack spacing={2.5} sx={{ width: '100%' }}>
				<Typography variant="body2" color="text.secondary">
					{t('spendPlan.conditions.editor.define')}
				</Typography>

				{/* Add section button */}
				<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
					<Button
						onClick={handleAddSection}
						size="small"
						startIcon={<IconGroup />}
						variant="outlined">
						{t('spendPlan.conditions.editor.addSection')}
					</Button>
					{verificationSections.length > 0 && (
						<Typography variant="caption" color="text.secondary">
							{t('spendPlan.conditions.editor.sectionCount', {
								count: verificationSections.length,
							})}
						</Typography>
					)}
				</Box>

				{/* Sections */}
				{verificationSections.length > 0 && (
					<Stack spacing={2}>
						{verificationSections.map((section, sectionIndex) => {
							const isExpanded = expandedSections[section.id] !== false;
							return (
								<Accordion
									key={section.id}
									expanded={isExpanded}
									onChange={() =>
										setExpandedSections((prev) => ({
											...prev,
											[section.id]: !isExpanded,
										}))
									}
									disableGutters
									sx={{
										border: '1px solid',
										borderColor: 'grey.300',
										borderRadius: '8px !important',
										'&:before': { display: 'none' },
										overflow: 'hidden',
									}}>
									<AccordionSummary
										expandIcon={<IconExpandMore />}
										sx={{
											bgcolor: 'grey.50',
											borderBottom: isExpanded ? '1px solid' : 'none',
											borderColor: 'grey.200',
											'& .MuiAccordionSummary-content': {
												alignItems: 'center',
												gap: 1,
											},
										}}>
										<IconGroup sx={{ color: 'primary.main', fontSize: 20 }} />
										<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
											{section.name || `Section ${sectionIndex + 1}`}
										</Typography>
										{section.assignedTo && (
											<Chip
												label={(() => {
													const u = projectUsers.find((u) => u.id === section.assignedTo);
													return u ? `${u.firstName} ${u.lastName}` : `User #${section.assignedTo}`;
												})()}
											color="primary"
											size="small"
											variant="twoTone"
											/>
										)}
										<Chip
											label={t('spendPlan.conditions.editor.sectionFields', {
												count: section.fields.length,
											})}
											color="default"
											size="small"
											variant="twoTone"
										/>
										<Box sx={{ flex: 1 }} />
										<IconButton
											onClick={(e) => {
												e.stopPropagation();
												handleRemoveSection(section.id);
											}}
											size="small"
											sx={{ color: 'grey.400', '&:hover': { color: 'error.main' } }}>
											<IconClose sx={{ fontSize: 16 }} />
										</IconButton>
									</AccordionSummary>
									<AccordionDetails sx={{ p: 2 }}>
										<Stack spacing={2}>
											{/* Section name & assignee */}
											<Box sx={{ display: 'flex', gap: 2 }}>
												<TextField
													fullWidth
													label={t('spendPlan.conditions.editor.sectionName')}
													onChange={(e) => handleUpdateSectionName(section.id, e.target.value)}
													placeholder="e.g., Finance Verification, Technical Review"
													size="small"
													value={section.name}
												/>
												<FormControl fullWidth size="small">
													<InputLabel>{t('spendPlan.conditions.editor.assignedTo')}</InputLabel>
													<Select
														label={t('spendPlan.conditions.editor.assignedTo')}
														onChange={(e) =>
															handleUpdateSectionAssignee(
																section.id,
																e.target.value ? Number(e.target.value) : null
															)
														}
														value={section.assignedTo || ''}>
														<MenuItem value="">
															<em>Unassigned</em>
														</MenuItem>
														{projectUsers.map((user) => (
															<MenuItem key={user.id} value={user.id}>
																{user.firstName} {user.lastName}
															</MenuItem>
														))}
													</Select>
												</FormControl>
											</Box>

											{/* Field toolbar */}
											{renderFieldTypeToolbar((fieldType) =>
												handleAddFieldToSection(section.id, fieldType)
											)}

											{/* Fields */}
											{section.fields.length === 0 ? (
												<Box
													sx={{
														display: 'flex',
														flexDirection: 'column',
														alignItems: 'center',
														py: 3,
														px: 2,
														bgcolor: 'grey.50',
														borderRadius: 2,
														border: '1px solid',
														borderColor: 'grey.200',
													}}>
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{ textAlign: 'center' }}>
														{t('spendPlan.conditions.editor.noRequirements')}
													</Typography>
												</Box>
											) : (
												<Stack spacing={1.5}>
													{section.fields.map((field, fieldIndex) =>
														renderFieldCard(field, fieldIndex, section.fields.length, {
															onMove: (fieldId, direction) => {
																const updated = verificationSections.map((s) => {
																	if (s.id !== section.id) return s;
																	const fields = [...s.fields];
																	const idx = fields.findIndex((f) => f.id === fieldId);
																	if (idx < 0) return s;
																	const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
																	if (targetIdx < 0 || targetIdx >= fields.length) return s;
																	[fields[idx], fields[targetIdx]] = [
																		fields[targetIdx],
																		fields[idx],
																	];
																	return { ...s, fields };
																});
																onChange(updated);
															},
															onRemove: (fieldId) =>
																handleRemoveFieldFromSection(section.id, fieldId),
															onUpdate: (fieldId, property, value) =>
																handleUpdateSectionField(section.id, fieldId, property, value),
															onUpdateOptions: (fieldId, optionsText) =>
																handleUpdateSectionFieldOptions(section.id, fieldId, optionsText),
														})
													)}
												</Stack>
											)}
										</Stack>
									</AccordionDetails>
								</Accordion>
							);
						})}
					</Stack>
				)}

				{/* Empty state */}
				{verificationSections.length === 0 && (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							py: 5,
							px: 3,
							bgcolor: 'grey.50',
							borderRadius: 2,
							border: '1px solid',
							borderColor: 'grey.200',
						}}>
						<IconDataRequest sx={{ fontSize: 40, color: 'grey.400', mb: 1 }} />
						<Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
							{t('spendPlan.conditions.editor.noRequirementsGlobal')}
						</Typography>
					</Box>
				)}

				{/* Override info */}
				{verificationSections.some((s) => s.fields.some((f) => f.allowOverride)) && (
					<Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
						<Typography variant="body2">
							Fields marked as overridable allow verifiers to skip the requirement by providing a
							justification comment.
						</Typography>
					</Alert>
				)}
			</Stack>

			{/* Field Settings Dialog */}
			{settingsField && (
				<DialogFieldSettings
					field={settingsField}
					open={Boolean(settingsField)}
					onSave={handleFieldSettingsSave}
					onClose={closeFieldSettings}
				/>
			)}
		</>
	);
};
